import React from 'react';
import styled from 'styled-components';
import { Avatar, IconButton } from '@mui/material';
import CustomCheckbox from '../Checkbox/CustomCheckbox';
import { AcceptIcon, ActiveIcon, MarketplaceShopIcon, PermissionIcon2, RejectIcon } from '../Icons';

interface OrderCardProps {
	item: any;
	avatarUrl?: string;
	isSelected?: (orderId: string) => boolean;
	handleCheckboxChange?: any;
	viewDetails?: (item: any, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
	view?: 'default' | 'team' | 'approval';
	isContainCheckbox?: boolean;
}

const Card = styled.div`
	display: flex;
	flex-direction: column;
	#card {
		display: flex;
		gap: 12px;
		padding: 16px;
		border: 1.5px solid #f1f1f1;
		width: 100%;
		background: #ffffff;
		position: relative;
	}
`;

const CheckboxContainer = styled.div`
	position: absolute;
	top: 8px;
`;

const CardContent = styled.div<{ isContainCheckbox: boolean }>`
	margin-left: ${(props) => (props.isContainCheckbox ? '50px' : '0')};
	display: flex;
	gap: 14px;

	.icon-container {
		display: flex;
		align-items: center;
		justify-content: center;
		background: #f7f5f4;
		width: 36px;
		height: 36px;
		border-radius: 50%;
		transition: background 0.3s ease;

		&:hover {
			background: #d1d1d1;
		}

		&:active {
			background: #a1a1a1;
		}
	}
`;

const Details = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	min-width: 220px;
`;

const Header = styled.div`
	display: flex;
	justify-content: space-between;
`;

const Name = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: #000000;
	max-width: 160px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const StyledAvatar = styled(Avatar)`
	width: 40px;
	height: 40px;
`;

const StyledID = styled.div`
	display: flex;
	background: #f1f1f1;
	padding: 10px 16px;
	border-bottom-left-radius: 10px;
	border-bottom-right-radius: 10px;
	width: 100%;
	span {
		font-size: 14px;
		font-weight: 500;
		color: #5c5c5c;
		max-width: 200px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
`;

const Fields = styled.div`
	font-size: 12px;
	font-weight: 500;
	color: #5c5c5c;
	max-width: 180px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const SettingsCard: React.FC<OrderCardProps> = ({
	item,
	avatarUrl,
	isSelected,
	handleCheckboxChange,
	viewDetails,
	view = 'default',
	isContainCheckbox = false,
}) => {
	const contentViews = () => {
		if (view === 'team') {
			return (
				<CardContent isContainCheckbox={isContainCheckbox}>
					<StyledAvatar src={avatarUrl}>{!avatarUrl && item?.firstname?.charAt(0)}</StyledAvatar>
					{item?.activeUser ? (
						<div style={{ position: 'absolute', top: '38px', left: '90px' }}>
							<ActiveIcon />
						</div>
					) : null}
					<Details>
						<Header>
							<Name>{item?.firstname}</Name>
							<IconButton
								style={{ position: 'absolute', top: '6px', right: '14px' }}
								onClick={item?.onClick}
							>
								<PermissionIcon2 id="permission" />
							</IconButton>
						</Header>
						<Fields>Email: {item?.email}</Fields>
						<div style={{ display: 'flex', gap: '10px' }}>
							<Fields>Phone No: {item?.telephone}</Fields>
							<Fields>Type: {item?.type}</Fields>
						</div>
					</Details>
				</CardContent>
			);
		} else if (view === 'approval') {
			return (
				<CardContent isContainCheckbox={isContainCheckbox}>
					<StyledAvatar src={avatarUrl}>{!avatarUrl && item?.name?.charAt(0)}</StyledAvatar>
					<Details>
						<Header>
							<Name>{item?.name}</Name>
						</Header>
						<Fields
							style={{
								maxWidth: '160px',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								display: '-webkit-box',
								WebkitBoxOrient: 'vertical',
								WebkitLineClamp: 3,
								lineHeight: '1.2em', // Adjust line height as needed
								height: '3.6em', // line-height * 3
								whiteSpace: 'normal',
							}}
						>
							Address: {item?.address}
						</Fields>
					</Details>
					{!item?.approved_by_supplier ? (
						<div
							id="btns-container"
							style={{
								display: 'flex',
								gap: '8px',
								alignItems: 'center',
								position: 'absolute',
								right: '14px',
								bottom: '16px',
							}}
						>
							<div
								id="button-1"
								className="icon-container"
								onClick={() => item?.acceptDialog(item)}
							>
								<AcceptIcon id="button-accept" />
							</div>

							<div
								id="button-2"
								className="icon-container"
								onClick={() => item?.rejectDialog(item)}
							>
								<RejectIcon id="button-reject" />
							</div>
						</div>
					) : (
						<span
							style={{
								color: '#979797',
								fontSize: '13px',
								fontWeight: 700,
								position: 'absolute',
								right: '10px',
								bottom: '16px',
							}}
						>
							Approved in 24 hrs
						</span>
					)}
				</CardContent>
			);
		}
		return (
			<CardContent isContainCheckbox={isContainCheckbox}>
				<StyledAvatar src={avatarUrl}>{!avatarUrl && item?.name?.charAt(0)}</StyledAvatar>
				<Details>
					<Header>
						<Name>{item?.name}</Name>
					</Header>
					<Fields>Branch No: {item?.branchId}</Fields>
					<Fields>Address: {item?.address}</Fields>
				</Details>
			</CardContent>
		);
	};
	return (
		<Card onClick={viewDetails ? (e) => viewDetails(item, e) : () => {}}>
			<StyledID>
				<span>ID: {item?.id}</span>
			</StyledID>
			<div id="card">
				{isContainCheckbox ? (
					<CheckboxContainer>
						<CustomCheckbox
							isChecked={isSelected ? isSelected(item.id) : false}
							handleChange={(e: any) => handleCheckboxChange(item?.id, e)}
						/>
					</CheckboxContainer>
				) : null}
				{contentViews()}
			</div>
		</Card>
	);
};

export default SettingsCard;
